/**
 * Helpers for the chat composer's inline image attachments.
 *
 * Images are sent inline as base64 in the chat-stream request body (no upload
 * round-trip yet — S3 presign is a later upgrade). These limits mirror the
 * backend validation in usesky-api's StreamChatMessageDto.
 */

export const CHAT_IMAGE_ALLOWED_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
] as const;

export type ChatImageMimeType = (typeof CHAT_IMAGE_ALLOWED_MIME_TYPES)[number];

/** Maximum decoded image size accepted by the backend (5 MB). */
export const CHAT_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

export interface ChatImageAttachment {
  /** Base64-encoded image data, without a `data:` URI prefix. */
  base64: string;
  mimeType: ChatImageMimeType;
  name: string;
}

export type ChatImageError = "invalidType" | "tooLarge" | "readFailed";

export function isAllowedChatImageType(
  type: string
): type is ChatImageMimeType {
  return (CHAT_IMAGE_ALLOWED_MIME_TYPES as readonly string[]).includes(type);
}

/** Reconstructs a renderable data URL from a stored base64 image. */
export function chatImageDataUrl(base64: string, mimeType: string): string {
  return `data:${mimeType};base64,${base64}`;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.onload = () => {
      const { result } = reader;
      if (typeof result !== "string") {
        reject(new Error("Unexpected FileReader result"));
        return;
      }
      const commaIndex = result.indexOf(",");
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

type ReadChatImageResult =
  | { attachment: ChatImageAttachment }
  | { error: ChatImageError };

/** Validates type/size and reads a file into an inline base64 attachment. */
export async function readChatImageFile(
  file: File
): Promise<ReadChatImageResult> {
  if (!isAllowedChatImageType(file.type)) {
    return { error: "invalidType" };
  }
  if (file.size > CHAT_IMAGE_MAX_BYTES) {
    return { error: "tooLarge" };
  }
  try {
    const base64 = await fileToBase64(file);
    return { attachment: { base64, mimeType: file.type, name: file.name } };
  } catch {
    return { error: "readFailed" };
  }
}
